import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { FC } from "react";
import { Option } from "types/option";

type LikertScaleSelectorProps = {
  value?: number | null;
  onChange?: (value: number) => void;
  options: Option[];
};

export const LikertScaleSelector: FC<LikertScaleSelectorProps> = ({
  value,
  onChange,
  options,
}) => {
  const sortedOptions = [...options].sort((a, b) => (a.order || 0) - (b.order || 0));

  const handleChange = (_: React.MouseEvent<HTMLElement>, newValue: number | null) => {
    if (typeof newValue === "number") {
      onChange?.(newValue);
    }
  };

  // Extract emoji from option text (format: "😢 Very Dissatisfied" -> "😢")
  const getEmoji = (text: string) => {
    // Match emoji at the start of the string
    const emojiMatch = text.match(/^[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u);
    return emojiMatch ? emojiMatch[0] : "";
  };

  // Extract label text without emoji
  const getLabel = (text: string) => {
    // Remove emoji and trim
    return text.replace(/^[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]\s*/u, "").trim();
  };

  // Check if any option has an emoji
  const hasEmojis = sortedOptions.some((opt) => getEmoji(opt.text) !== "");

  return (
    <Box sx={{ mt: 2 }}>
      <ToggleButtonGroup
        color="primary"
        value={value !== undefined && value !== null ? value : null}
        exclusive
        onChange={handleChange}
        fullWidth
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: "wrap",
          gap: 1,
          "& .MuiToggleButton-root": {
            flex: { xs: "1 1 100%", sm: 1 },
            minWidth: { xs: "100%", sm: 0 },
            py: { xs: 1.25, sm: hasEmojis ? 2 : 1.5 },
            px: { xs: 1.5, sm: 1 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            flexDirection: hasEmojis ? "column" : "row",
            "&.Mui-selected": {
              backgroundColor: "primary.main",
              color: "white",
              fontWeight: 600,
              borderColor: "primary.main",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            },
            "&:hover": {
              backgroundColor: "action.hover",
            },
          },
        }}
      >
        {sortedOptions.map((opt, index) => {
          const emoji = getEmoji(opt.text);
          const label = getLabel(opt.text);
          return (
            <ToggleButton value={index} key={opt.option_id || index}>
              {emoji && (
                <Typography
                  variant="h4"
                  component="span"
                  sx={{
                    fontSize: { xs: "1.5rem", sm: "2rem" },
                    mb: hasEmojis ? 0.5 : 0,
                  }}
                >
                  {emoji}
                </Typography>
              )}
              <Typography
                variant="body2"
                component="span"
                sx={{
                  textTransform: "none",
                  textAlign: "center",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  lineHeight: 1.2,
                }}
              >
                {label || opt.text}
              </Typography>
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
    </Box>
  );
};
